import React, { useCallback, useState, useEffect } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  message,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import {
  useGetMarketplaceOrderQuery,
  useGetUsimOrderIccidInventoryQuery,
  useGetUsimInventoryByMobileQuery,
  usePostUsimOrdersMutation
} from "../../../../api/usim/orders/UsimOrdersApi";
import { useGetUsimSkuByTypeQuery } from "../../../../api/usim/uploads/products/productSkuApi";
import { useGetProductTypesQuery } from "../../../../api/usim/uploads/products/productTypeApi";
import { MarketOrderItem } from "../../../../api/usim/orders/MarketOrderItem";
import { UsimInventoryItem } from "../../../../api/usim/inventory/inventoryItem";
import { Auth } from 'aws-amplify';
import { UsimOrderSubmitData } from "../../../../api/usim/orders/UsimOrderItem";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { generateOrderNumber, getInventoryStatus, TableRowData } from "./orderUtilities";
import { USimStatus, toUSimStatus } from "../../shared/usimUtils";
import { UsimSkuDropdown } from "../../shared/usimSkuDropdown";

const { TextArea } = Input;
const { Title } = Typography;

interface RetailOrderFormData {
  orderProcessedDate: Dayjs | null;
  orderNumber: string;
  quantity: number;
  activationDate: Dayjs | null;
  productType: string;
  productPlan: string;
  comment: string;
  isRecharge: boolean;
  instantRecharge: boolean;
}

type InventoryLookupRequest = {
  rowKey: string;
  field: "iccid" | "mobileNumber";
  value: string;
};

const defaultFormValues: RetailOrderFormData & {
  planRecharge: boolean;
  instantRecharge: boolean;
} = {
  orderProcessedDate: dayjs(),
  orderNumber: "",
  quantity: 0,
  activationDate: null,
  productType: "",
  productPlan: "",
  comment: "",
  isRecharge: false,
  planRecharge: false,
  instantRecharge: false,
};


export default function RetailOrderPage() {
  const [form] = Form.useForm();
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [orderLookupNumber, setOrderLookupNumber] = useState<string>("");
  const [inventoryLookupRequest, setInventoryLookupRequest] = useState<InventoryLookupRequest | null>(null);
  const [postUsimOrders, { isLoading: isSubmitting }] = usePostUsimOrdersMutation();
  const [marketOrder, setMarketOrder] = useState<MarketOrderItem>({} as MarketOrderItem);
  const [isSkuPresent, setIsSkuPresent] = useState<string>("");

  // auto recharge variables
  const [isRechargePlan, setIsRechargePlan] = useState<boolean>(false);
  const [isInstantRecharge, setIsInstantRecharge] = useState<boolean>(false);
  const watchedQuantity = Form.useWatch("quantity", form);
  const watchedProductType = Form.useWatch("productType", form);
  const watchedProductPlan = Form.useWatch("productPlan", form);
  const watchedActivationDate = Form.useWatch("activationDate", form);

  // RTK Query hook for fetching order by ID
  const { data: orderData, error: orderError, isFetching: isOrderFetching } = useGetMarketplaceOrderQuery(
    orderLookupNumber,
    { skip: !orderLookupNumber, refetchOnMountOrArgChange: true } // Skip the query if order number is empty

  );
  // RTK Query hook for fetching ICCID inventory details
  const currentIccidLookup = inventoryLookupRequest?.field === "iccid" ? inventoryLookupRequest.value : "";
  const currentMobileLookup = inventoryLookupRequest?.field === "mobileNumber" ? inventoryLookupRequest.value : "";
  const {
    data: iccidInventory,
    error: iccidInventoryError,
    isFetching: isIccidInventoryFetching,
  } = useGetUsimOrderIccidInventoryQuery(
    currentIccidLookup,
    { skip: !currentIccidLookup } // Skip the query if no ICCID is being looked up
  );
  // RTK Query hook for fetching mobile inventory details
  const {
    data: mobileInventory,
    error: mobileInventoryError,
    isFetching: isMobileInventoryFetching,
  } = useGetUsimInventoryByMobileQuery(
    currentMobileLookup,
    { skip: !currentMobileLookup } // Skip the query if no mobile number is being looked up
  );
  const isInventoryFetching = isIccidInventoryFetching || isMobileInventoryFetching;

  // once selected it triggers type api to load the plans for that type.
  const [selectedProductType, setSelectedProductType] = useState<string>("");
  // load the plans
  const {
    data: productPlansForType,
    isFetching: productPlansForTypeFetching
  } = useGetUsimSkuByTypeQuery(
    selectedProductType,
    { skip: !selectedProductType }
  );
  const { data: productTypes } = useGetProductTypesQuery();


  // Transform product types for dropdown options
  const productTypesOptions = React.useMemo(() => {
    if (!productTypes) return [];
    //do not show item if status is INACTIVE
    return productTypes.filter(item => item.status !== false).map(item => ({
      label: item.name,
      value: item.name,
      iccid: item.iccid,
      id: item.id,
      hasActivation: item.activationDate ?? false
    }));
  }, [productTypes]);

  // Transform product plans for dropdown options
  const productPlansOptions = React.useMemo(() => {
    if (!productPlansForType) return [];
    //do not show item if status is INACTIVE
    return productPlansForType.filter(item => item.status).map(item => ({
      label: item.name,
      value: item.name,
      type: item.typeName
    }));
  }, [productPlansForType]);

  useEffect(() => {
    if (isSkuPresent !== "") {
      message.warning('SKU is Not Available in System: ' + isSkuPresent);
    }
  }, [isSkuPresent]);

  function getProductType(orderData: any, skuName: string) {
    if (orderData.sku_details) {
      const skuDetail = orderData.sku_details[skuName] ?? {};
      if (skuDetail.typeName) {
        return skuDetail.typeName;
      }
    }
    return "";
  }
  // Handle API response for orders
  useEffect(() => {
    if (orderData) {
      // Extract data from the order
      let totalQuantity = 0;
      let skuItems: { sku: string; quantity: number; type?: string }[] = [];

      // Check if OrderDetails and item_list exist
      if (orderData.OrderDetails && orderData.OrderDetails.item_list) {
        // Loop through all items and accumulate quantities
        orderData.OrderDetails.item_list.forEach((skuItem: any) => {
          if (skuItem.model_quantity_purchased && skuItem.model_sku) {
            if (skuItem.model_sku.toLowerCase().includes("-esim-")) {
              return;
            }
            const qty = Number(skuItem.model_quantity_purchased);
            totalQuantity += qty;
            const type = getProductType(orderData, skuItem.model_sku) || "";
            // const type = productPlansOptions.find(x => x.value === skuItem.model_sku)?.type || "";
            skuItems.push({
              sku: skuItem.model_sku,
              quantity: qty,
              type: type
            });
          }
        });
      }
      else{
        skuItems = []
      }

      setMarketOrder({ ...orderData, ShopId: String(orderData.ShopId) });
      const nextFormValues = {
        orderNumber: orderData.OrderId || "",
        quantity: totalQuantity || 0,
        productPlan: skuItems[0]?.sku || "",
        productType: skuItems[0]?.type || "",
      };
      form.setFieldsValue({
        ...nextFormValues,
        orderProcessedDate: form.getFieldValue("orderProcessedDate") || dayjs(),
      });

      // Update table data with correct quantities for each SKU
      let rowIndex = 0;
      const newTableData: TableRowData[] = [];
      const missingSku = skuItems.find(item => !orderData.sku_details?.[item.sku]);
      setIsSkuPresent(missingSku?.sku ?? "");

      // Create rows for each SKU and its quantity
      skuItems.forEach(item => {
        const productTypeItem = productTypesOptions.find(opt => opt.value === item.type);
        const hasActivation = productTypeItem?.hasActivation ?? false;

        for (let i = 0; i < item.quantity; i++) {
          newTableData.push({
            key: `row-${rowIndex}`,
            iccid: "",
            mobileNumber: "",
            productType: item.type || "",
            productPlan: item.sku,
            status: USimStatus.PENDING_PROCESSING,
            activationDate: null,
            inventoryId: "",
            puk: "",
            hasActivation: hasActivation,
          });
          rowIndex++;
        }
      });

      setTableData(newTableData);

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData]);

  useEffect(() => {
    if (!productTypesOptions.length) {
      return;
    }

    setTableData(prev => prev.map(row => {
      const productTypeItem = productTypesOptions.find(opt => opt.value === row.productType);
      if (!productTypeItem || row.hasActivation === productTypeItem.hasActivation) {
        return row;
      }
      return {
        ...row,
        hasActivation: productTypeItem.hasActivation,
      };
    }));
  }, [productTypesOptions]);

  // Show error message if API call fails
  useEffect(() => {
    if (orderError) {
      message.error('Failed to fetch order details');

    }
  }, [orderError]);

  const clearProductRow = useCallback((rowKey: string, clearIccid = false) => {
    setTableData(prev => prev.map(row => {
      if (row.key === rowKey) {
        return {
          ...row,
          iccid: clearIccid ? "" : row.iccid,
          mobileNumber: "",
          productType: "",
          status: USimStatus.PENDING_PROCESSING,
          inventoryId: "",
        };
      }
      return row;
    }));
  }, []);

  const validIccidToUse = useCallback((inventoryItem:UsimInventoryItem, rowKey: string) => {
    if (inventoryItem.ordernumber) {
      const isSameOrder = Array.isArray(inventoryItem.ordernumber) 
        ? inventoryItem.ordernumber.includes(orderLookupNumber)
        : inventoryItem.ordernumber === orderLookupNumber;

      if (isSameOrder || isRechargePlan) {
        return true;
      }

      Modal.confirm({
        title: "ICCID is Already Used in the order",
        content: <div>
          <p>Order Number: {Array.isArray(inventoryItem.ordernumber) ? inventoryItem.ordernumber.join(", ") : inventoryItem.ordernumber}</p>
          <p>ICCID: {inventoryItem.ICCID}</p>
        </div>
      })
      // clear the prefilled for this row only
      clearProductRow(rowKey);
      return false;
    }
    return true;
  }, [clearProductRow, isRechargePlan, orderLookupNumber]);

  const setInventoryData = useCallback((inventoryItem: UsimInventoryItem, lookupRequest: InventoryLookupRequest) => {
    if(!validIccidToUse(inventoryItem, lookupRequest.rowKey)){
      return;
    }
    // Determine status based on ICCID data
    let status = getInventoryStatus(productTypesOptions,inventoryItem);
    
    setTableData(prev => prev.map((row) => {
      if (row.key === lookupRequest.rowKey) {
        // For mobile number lookup, update ICCID without triggering the ICCID API
        const nextIccid = lookupRequest.field === "mobileNumber" ? String(inventoryItem.ICCID || "") : row.iccid;
        const productTypeData = productTypesOptions?.find(x => x.id === inventoryItem.typeId);
        const localProductType = productTypeData?.label || "";
        const hasActivation = productTypeData?.hasActivation ?? row.hasActivation;
        const baseUpdate = {
          ...row,
          iccid: nextIccid,
          mobileNumber: inventoryItem.mobileNumber || "",
          status: toUSimStatus(status),
          productType:  localProductType || "",
          productPlan: row.productPlan,
          inventoryId: inventoryItem.Id || "",
          activationDate: row.activationDate, // Preserve existing activation date
          hasActivation: hasActivation,
        };

        if (!inventoryItem.planName || inventoryItem.planName === "-") {
          //load the plan names for dropdown for selected type
          var tempTypeName = productTypes?.find(x => x.id === inventoryItem.typeId)?.name || "";
          setSelectedProductType(tempTypeName);
        }

        return baseUpdate;
      }
      return row;
    }));
  }, [productTypes, productTypesOptions, validIccidToUse]);
  // Handle ICCID inventory data
  useEffect(() => {
    if (!inventoryLookupRequest || inventoryLookupRequest.field !== "iccid" || isIccidInventoryFetching) {
      return;
    }

    if (iccidInventory && iccidInventory.length > 0) {
      const inventoryItem = iccidInventory.find(x => x.ICCID?.toString() === String(inventoryLookupRequest.value));
      if (inventoryItem) {
        setInventoryData(inventoryItem, inventoryLookupRequest);
        setInventoryLookupRequest(null);
      }
      else {
        clearProductRow(inventoryLookupRequest.rowKey);
        message.warning('No inventory found for this ICCID');
        setInventoryLookupRequest(null);
      }

    } else if (iccidInventory && iccidInventory.length === 0) {
      // Clear row data if API returns empty result
      clearProductRow(inventoryLookupRequest.rowKey);
      setInventoryLookupRequest(null);
      message.warning('No inventory found for this ICCID');

    }
  }, [clearProductRow, iccidInventory, inventoryLookupRequest, isIccidInventoryFetching, setInventoryData]);

  useEffect(() => {
    if (!inventoryLookupRequest || inventoryLookupRequest.field !== "mobileNumber" || isMobileInventoryFetching) {
      return;
    }

    if (mobileInventory && mobileInventory.length > 0) {
      const inventoryItem = mobileInventory.find(x => String(x.mobileNumber || "") === String(inventoryLookupRequest.value));
      if (inventoryItem) {
        setInventoryData(inventoryItem, inventoryLookupRequest);
        setInventoryLookupRequest(null);
      } else {
        clearProductRow(inventoryLookupRequest.rowKey, true);
        setInventoryLookupRequest(null);
        message.warning('No inventory found for this mobile number');
      }
    } else if (mobileInventory && mobileInventory.length === 0) {
      // Clear row data if API returns empty result
      clearProductRow(inventoryLookupRequest.rowKey, true);
      setInventoryLookupRequest(null);
      message.warning('No inventory found for this mobile number');
    }
  }, [clearProductRow, inventoryLookupRequest, isMobileInventoryFetching, mobileInventory, setInventoryData]);

  // Show error message if ICCID lookup fails
  useEffect(() => {
    if (iccidInventoryError && inventoryLookupRequest?.field === "iccid") {
      // Clear row data if API returns error
      clearProductRow(inventoryLookupRequest.rowKey);
      setInventoryLookupRequest(null);
      message.error('Failed to fetch ICCID details');
    }
  }, [clearProductRow, iccidInventoryError, inventoryLookupRequest]);

  // Show error message if mobile number lookup fails
  useEffect(() => {
    if (mobileInventoryError && inventoryLookupRequest?.field === "mobileNumber") {
      // Clear row data if API returns error
      clearProductRow(inventoryLookupRequest.rowKey, true);
      setInventoryLookupRequest(null);
      message.error('Failed to fetch mobile number details');
    }
  }, [clearProductRow, inventoryLookupRequest, mobileInventoryError]);

  const handleDeleteRow = (key: string) => {
    setTableData(prev => prev.filter(row => row.key !== key));
  };

  // Generate table rows based on quantity while preserving existing data
  useEffect(() => {
    const quantity = Number(watchedQuantity || 0);
    const formProductType = watchedProductType || "";
    const formProductPlan = watchedProductPlan || "";
    const formActivationDate = watchedActivationDate || null;

    setTableData(prevData => {
      const newRows: TableRowData[] = [];
      for (let i = 0; i < quantity; i++) {
        // If row exists, preserve its data
        if (i < prevData.length) {
          const formattedFormDataActivationDate = formActivationDate && typeof (formActivationDate as any).format === 'function' ? (formActivationDate as any).format('DD-MMM-YYYY') : formActivationDate;
          const updatedActivationDate = prevData[i].activationDate || formattedFormDataActivationDate;
          let updatedStatus = prevData[i].status;

          if (formattedFormDataActivationDate && !prevData[i].activationDate) {
            updatedStatus = USimStatus.PENDING_PROCESSING;
          }

          const newProductType = prevData[i].productType || formProductType || "";
          const productTypeItem = productTypesOptions.find(opt => opt.value === newProductType);
          const hasActivation = prevData[i].productType ? prevData[i].hasActivation : (productTypeItem?.hasActivation ?? false);

          newRows.push({
            ...prevData[i],
            // Only update these fields if they're empty
            productType: newProductType,
            productPlan: prevData[i].productPlan || formProductPlan || "",
            activationDate: updatedActivationDate,
            status: updatedStatus,
            hasActivation: hasActivation
          });
        } else {
          const productTypeItem = productTypesOptions.find(opt => opt.value === formProductType);
          const hasActivation = productTypeItem?.hasActivation ?? false;

          // Add new row with default values
          newRows.push({
            key: `row-${i}`,
            iccid: "",
            mobileNumber: "",
            productType: formProductType || "",
            productPlan: formProductPlan || "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: formActivationDate,
            inventoryId: "",
            puk: "",
            hasActivation: hasActivation,
          });
        }
      }
      return newRows;
    });
  }, [productTypesOptions, watchedActivationDate, watchedProductPlan, watchedProductType, watchedQuantity]);

  // Handle order number input blur
  const handleOrderNumberBlur = () => {
    const nextOrderNumber = String(form.getFieldValue("orderNumber") || "").trim();
    if (nextOrderNumber && nextOrderNumber !== orderLookupNumber) {
      setOrderLookupNumber(nextOrderNumber);
    }
  };

  const handleSubmit = async (values: RetailOrderFormData) => {
    if (!values.orderNumber) {
      message.error('Order number is required');
      return;
    }

    if (!values.quantity || values.quantity <= 0) {
      message.error('Quantity must be greater than 0');
      return;
    }

    // Check if all required ICCIDs are filled
    // const filledRows = tableData.filter(row => row.iccid.trim() !== '');
    // if (filledRows.length !== values.quantity) {
    //   message.error(`Please fill in all ${values.quantity} ICCID(s)`);
    //   return;
    // }

    const now = new Date().toISOString();
    const order_status = tableData.length > 0 && tableData.every(row => toUSimStatus(row.status) === USimStatus.COMPLETE)
      ? USimStatus.COMPLETE
      : USimStatus.PENDING_PROCESSING;
    let session = await Auth.currentUserInfo();
    const uname = session.username || "";
    // console.log("data in type", productTypes[0])
   
    const submitData: UsimOrderSubmitData = {
      OrderNumber: values.orderNumber,
      orderProcessedDate: values.orderProcessedDate?.toISOString() || now,
      type: values.productType,
      description: values.comment || '',
      sku: values.productPlan,
      quantity: values.quantity,
      status: order_status,
      processedBy: uname || "",
      productDetails: tableData.map(row => ({
        iccid: row.iccid,
        mobileNumber: row.mobileNumber,
        productType: row.productType,
        sku: row.productPlan,
        status: toUSimStatus(row.status),
        activationDate: row.activationDate ? dayjs(row.activationDate).format('YYYY-MM-DD') : undefined,
        inventoryId: row.inventoryId,
        puk: row.puk,
        shopId: marketOrder.ShopId,
        hasActivation: row.hasActivation,
      })),
      isRecharge: isRechargePlan,
      instantRecharge: isInstantRecharge,
      orderData: marketOrder,
    };

    if (submitData.productDetails.length === submitData.quantity && submitData.quantity > 0) {
      for (const productDetail of submitData.productDetails) {
        if (!productDetail.productType || productDetail.productType === "") {
          message.error('Product type is required for each product detail');
          return;
        }
      }
    }

    try {
      console.log("submti data", submitData)
      // await postUsimOrders(submitData).unwrap();
      message.success('Order submitted successfully');
      handleReset();

    } catch (error: any) {
      console.error('Error submitting order:', error);
      message.error(error.data.message || 'Failed to submit order');
    }
  };

  const onClickSubmit = async () => {
    try {
      const values = await form.validateFields();
      await handleSubmit(values);

      // Refresh page after 100ms to show clean state
      // setTimeout(() => window.location.reload(), 250);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Validation failed');
      }
    }
  };

  const handleReset = () => {
    form.resetFields();
    setInventoryLookupRequest(null);
    setIsSkuPresent("");
    setMarketOrder({} as MarketOrderItem);
    setOrderLookupNumber("");
    setIsRechargePlan(false);
    setIsInstantRecharge(false);
    // Explicitly set default values after reset
    form.setFieldsValue(defaultFormValues);

    setTableData([]);

  };

  const handleTableDataChange = (
    key: string,
    field: keyof TableRowData,
    value: any
  ) => {
    setTableData((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentKey: string,
    field: keyof TableRowData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Find current row index
      const currentIndex = tableData.findIndex((row) => row.key === currentKey);
      if (currentIndex === -1) return;

      // Find next row
      const nextRow = tableData[currentIndex + 1];
      if (nextRow) {
        // Focus on the same field in the next row
        const nextInput = document.querySelector(
          `[data-row-key="${nextRow.key}"][data-field="${field}"]`
        ) as HTMLElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleSelectKeyPress = (
    e: React.KeyboardEvent,
    currentKey: string,
    field: keyof TableRowData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Find current row index
      const currentIndex = tableData.findIndex((row) => row.key === currentKey);
      if (currentIndex === -1) return;

      // Find next row
      const nextRow = tableData[currentIndex + 1];
      if (nextRow) {
        // Focus on the same field in the next row
        const nextInput = document.querySelector(
          `[data-row-key="${nextRow.key}"][data-field="${field}"]`
        ) as HTMLElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const columns: ColumnsType<TableRowData> = [
    {
      title: " ",
      key: "action",
      width: 60,
      render: (_, record) => (
        <Button danger onClick={() => handleDeleteRow(record.key)}>
          <DeleteOutlined />
        </Button>
      ),
    },
    {
      title: "ICCID",
      dataIndex: "iccid",
      key: "iccid",
      width: 250,
      render: (_, record) => (
        <Input
          value={record.iccid}
          disabled={isInventoryFetching}
          onChange={(e) => {
            const value = e.target.value.trim();
            handleTableDataChange(record.key, "iccid", value);
          }}
          data-row-key={record.key}
          data-field="iccid"
          onKeyDown={(e) => handleKeyPress(e, record.key, "iccid")}
          onBlur={(e) => {
            const value = e.currentTarget.value.trim();
            handleTableDataChange(record.key, "mobileNumber", "");
            handleTableDataChange(record.key, "productType", "");
            handleTableDataChange(record.key, "status", USimStatus.PENDING_PROCESSING);
            if (value) {
              setInventoryLookupRequest({ rowKey: record.key, field: "iccid", value });
            } else {
              clearProductRow(record.key);
            }
          }}
        />
      ),
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 150,
      render: (_, record) => (
        <Input
          value={record.mobileNumber}
          disabled={isInventoryFetching}
          onChange={(e) => {
            const value = e.target.value.trim();
            handleTableDataChange(record.key, "mobileNumber", value);
          }}
          onKeyDown={(e) => handleKeyPress(e, record.key, "mobileNumber")}
          onBlur={(e) => {
            const value = e.currentTarget.value.trim();
            handleTableDataChange(record.key, "iccid", "");
            handleTableDataChange(record.key, "productType", "");
            handleTableDataChange(record.key, "status", USimStatus.PENDING_PROCESSING);
            if (value) {
              setInventoryLookupRequest({ rowKey: record.key, field: "mobileNumber", value });
            } else {
              clearProductRow(record.key, true);
            }
          }}
          placeholder="Enter mobile number"
          data-row-key={record.key}
          data-field="mobileNumber"
        />
      ),
    },
    {
      title: "Product Type",
      dataIndex: "productType",
      key: "productType",
      width: 200,
      render: (_, record) => (
        <Select
          value={record.productType}
          onChange={(value) => {
            handleTableDataChange(record.key, "productType", value);
            let data = productTypesOptions.find(item => item.value === value);
            if (data && (data.iccid === false || data.iccid.toString().toLowerCase() === "false")) {
              handleTableDataChange(record.key, "status", USimStatus.COMPLETE);
            }
            const hasActivation = data?.hasActivation ?? false;
            handleTableDataChange(record.key, "hasActivation", hasActivation);
            setSelectedProductType(value);
          }}
          onKeyDown={(e) => handleSelectKeyPress(e as any, record.key, "productType")}
          options={productTypesOptions}
          placeholder="Select product type"
          style={{ width: "100%" }}
          data-row-key={record.key}
          data-field="productType"
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: "Product SKU",
      dataIndex: "productPlan",
      key: "productPlan",
      width: 220,
      render: (_, record) => (
        <UsimSkuDropdown
          value={record.productPlan}
          placeholder="Select product SKU"
          style={{ width: "100%" }}
          onChange={(value) =>
            handleTableDataChange(record.key, "productPlan", value as string)
          }
          typeName={record.productType}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (_, record) => (
        <Select
          value={toUSimStatus(record.status)}
          onChange={(value) =>
            handleTableDataChange(record.key, "status", value)
          }
          onKeyDown={(e) => handleSelectKeyPress(e as any, record.key, "status")}
          placeholder="Select status"
          style={{ width: "100%" }}
          data-row-key={record.key}
          data-field="status"
          options={[
            { value: USimStatus.PENDING_PROCESSING, label: "Pending-Processing" },
            { value: USimStatus.COMPLETE, label: "Completed" },
          ]}
        />
      ),
    },
    {
      title: "Activation Date",
      dataIndex: "activationDate",
      key: "activationDate",
      width: 140,
      render: (_, record) => (
        <DatePicker
          value={record.activationDate ? dayjs(record.activationDate) : null}
          onChange={(date) => {
            const dateStr = date ? date.format('DD-MMM-YYYY') : '';
            handleTableDataChange(record.key, "activationDate", dateStr as any);
            if (dateStr) {
              handleTableDataChange(record.key, "status", USimStatus.PENDING_PROCESSING);
            }
          }}
          style={{ width: "100%" }}
          placeholder="Select activation date"
          data-row-key={record.key}
          data-field="activationDate"
        />
      ),
    },
    {
      title: "PUK/PIN",
      dataIndex: "puk",
      key: "puk",
      width: 100,
      render: (_, record) => (
        <Input
          value={record.puk}
          onChange={(e) => {
            const value = e.target.value.trim();
            handleTableDataChange(record.key, "puk", value);
          }}
          onKeyDown={(e) => handleKeyPress(e, record.key, "puk")}
          placeholder="Enter PUK/PIN"
          data-row-key={record.key}
          data-field="puk"
        />
      ),
    },
  ];

 

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2} style={{color:"red"}}>Create New Retail Order</Title>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={defaultFormValues}
        onFinish={handleSubmit}
        style={{ maxWidth: "85%" }}
      >
        {orderError && (
          <Alert
            type="error"
            message="Failed to fetch order details"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {(iccidInventoryError || mobileInventoryError) && (
          <Alert
            type="error"
            message="Failed to fetch inventory details"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Order Processed Date" name="orderProcessedDate" getValueProps={(i) => ({ value: i ? dayjs(i) : null })}>
              <DatePicker style={{ width: "80%" }} placeholder="Select date" format="DD-MMM-YYYY" />
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      <Tooltip title="Select if this order is a top-up (second recharge)." style={{ cursor: 'pointer', color: '#111111', backgroundColor: '#F2F2F2' }}>
                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                      </Tooltip>
                      Recharge Plan
                    </span>
                  }
                  name="planRecharge"
                  labelCol={{ span: 16 }}
                  wrapperCol={{ span: 8 }}
                >
                  <Checkbox checked={isRechargePlan} onChange={(e) => {
                    const checked = e.target.checked;
                    setIsRechargePlan(checked);
                    if (checked) {
                      const newOrderNumber = generateOrderNumber();
                      form.setFieldsValue({ orderNumber: newOrderNumber });
                    } else {
                      setOrderLookupNumber("");
                      form.setFieldsValue({ orderNumber: "" });
                    }
                  }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      <Tooltip title="Select to load the plan immediately (for Joy blank SIMs only).">
                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                      </Tooltip>
                      Instant Recharge
                    </span>
                  }
                  name="instantRecharge"
                  labelCol={{ span: 16 }}
                  wrapperCol={{ span: 8 }}
                >
                  <Checkbox checked={isInstantRecharge} onChange={(e) => setIsInstantRecharge(e.target.checked)} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Order Number" name="orderNumber" rules={[{ required: true, message: "Order Number is required" }]}>
              <Input
                style={{ width: "80%" }}
                placeholder="Enter order number"
                disabled={isOrderFetching}
                onBlur={handleOrderNumberBlur}
                suffix={isOrderFetching ? "Loading..." : null}
              />
            </Form.Item>

            <Form.Item
              label="Quantity"
              name="quantity"
              rules={[{ required: true, message: "Quantity is required" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "80%" }}
                max={150}
                placeholder="Enter quantity"
              />
            </Form.Item>

            <Form.Item label="Activation Date" name="activationDate">
              <DatePicker
                style={{ width: "80%" }}
                placeholder="Select activation date"
                format="DD-MMM-YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Product Type" name="productType">
              <Select
                options={productTypesOptions}
                placeholder="Select product Type"
                showSearch
                style={{ width: "80%" }}
                onChange={(value) => setSelectedProductType(value)}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label="Product SKU" name="productPlan" className="w-full">
              <Select
                options={productPlansOptions}
                placeholder="Select product SKU"
                style={{ width: "80%" }}
                loading={productPlansForTypeFetching}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label="Comment" name="comment">
              <TextArea rows={3} placeholder="Enter comments" style={{ width: "80%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Table Section */}
      <Title level={3}>Product Details</Title>
      <Table
        columns={columns}
        dataSource={tableData}
        loading={isOrderFetching || isInventoryFetching}
        pagination={false}
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: "Enter quantity above to add product details",
        }}
      />
      {/* Action Buttons */}
      <Flex gap={24} justify="flex-end" style={{ marginTop: 16 }}>
        <Button onClick={handleReset} disabled={isSubmitting || isOrderFetching || isInventoryFetching}>Reset</Button>
        <Button
          type="primary"
          onClick={onClickSubmit}
          loading={isSubmitting}
          disabled={isOrderFetching || isInventoryFetching}
          style={{ width: "120px" }}
        >
          Submit
        </Button>
      </Flex>
    </div>
  );
}
